import React, { FC, useState } from 'react';

import { Box, HStack, Stack } from '@chakra-ui/react';

import { getIconColor, InformationAlert } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import {
  DeleteAttributeIconButton,
  PrimaryButton,
  Select,
} from 'components/DataEntry';
import {
  ExclamationCircleIcon,
  GroupIcon,
  IconTypes,
  RoleIcon,
} from 'components/Icons';
import { addConditionButtonStyles } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/styles';

interface JitAccessItem {
  id: string;
  group: Record<string, any> | null;
  roles: Record<string, any>[];
}

interface Props {
  groupOptions: Record<string, any>[];
  cloudRoleOptions: Record<string, any>[];
  jitAccessItems: Record<string, JitAccessItem>;
  isGroupsLoading: boolean;
  isCloudRolesLoading: boolean;
  onAddItem: (group: Record<string, any>) => void;
  onRemoveItem: (item: JitAccessItem) => void;
  onUpdateItem: (
    item: JitAccessItem,
    field: 'group' | 'roles',
    value: Record<string, any> | Record<string, any>[],
  ) => void;
}

export const JitAccess: FC<Props> = ({
  groupOptions,
  cloudRoleOptions,
  jitAccessItems,
  isGroupsLoading,
  isCloudRolesLoading,
  onAddItem,
  onRemoveItem,
  onUpdateItem,
}) => {
  const [visibleGroupCount, setVisibleGroupCount] = useState(15);
  const [groupSearch, setGroupSearch] = useState('');

  const isGroupAvailable = (group: Record<string, any> | null) =>
    Boolean(
      group?.value &&
        groupOptions.some(
          option => String(option.value) === String(group.value),
        ),
    );
  const visibleJitAccessItems = Object.values(jitAccessItems).filter(
    item => !item.group?.value || isGroupAvailable(item.group),
  );

  const getGroupValue = (item: JitAccessItem) =>
    groupOptions.find(
      option => String(option.value) === String(item.group?.value),
    ) ?? item.group;

  const getGroupOptions = (item: JitAccessItem) => {
    const selectedGroupValues = new Set(
      Object.values(jitAccessItems)
        .filter(
          otherItem =>
            otherItem.id !== item.id && isGroupAvailable(otherItem.group),
        )
        .map(otherItem => otherItem.group?.value)
        .filter(Boolean)
        .map(value => String(value)),
    );

    const availableGroups = groupOptions.filter(
      option => !selectedGroupValues.has(String(option.value)),
    );

    if (groupSearch.trim()) {
      const search = groupSearch.toLowerCase();
      return availableGroups.filter(option =>
        String(option.label).toLowerCase().includes(search),
      );
    }

    const visibleGroups = availableGroups.slice(0, visibleGroupCount);
    const selectedGroup = groupOptions.find(
      option => String(option.value) === String(item.group?.value),
    );

    if (
      selectedGroup &&
      !visibleGroups.some(
        option => String(option.value) === String(selectedGroup.value),
      )
    ) {
      return [selectedGroup, ...visibleGroups];
    }

    return visibleGroups;
  };

  const handleAddItem = () => {
    const selectedGroupValues = new Set(
      Object.values(jitAccessItems)
        .map(item => item.group?.value)
        .filter(Boolean)
        .map(value => String(value)),
    );
    const defaultGroup = groupOptions.find(
      option => !selectedGroupValues.has(String(option.value)),
    );

    if (defaultGroup) onAddItem(defaultGroup);
  };

  const handleGroupChange = (
    item: JitAccessItem,
    value: Record<string, any> | null,
  ) => {
    onUpdateItem(item, 'group', value);
  };

  return (
    <Stack spacing={5}>
      <InformationAlert
        icon={<ExclamationCircleIcon color="primary" />}
        iconSize={6}
        spacing={2}
        py={3}
        w="80%"
        description="These authorized groups are allowed to request JIT or on-demand access to the indicated roles"
      />
      <BorderedBox p={0}>
        <HStack
          w="full"
          align="center"
          spacing={0}
          borderBottom="1px solid"
          borderColor="componentBorder"
          px={6}
          py={3}
          fontWeight={600}
        >
          <Box
            w={{ base: 'full', md: '38%' }}
            flexShrink={0}
            whiteSpace="nowrap"
          >
            Groups That Can Request Access
          </Box>
          <Box
            pl={2}
            w={{ base: 'full', md: '48%' }}
            flexShrink={0}
            whiteSpace="nowrap"
          >
            Roles That Can Be Requested
          </Box>
          <Box flex={1} display="flex" justifyContent="flex-end">
            <PrimaryButton
              {...addConditionButtonStyles}
              onClick={handleAddItem}
              isDisabled={
                isGroupsLoading ||
                !groupOptions.some(option =>
                  visibleJitAccessItems.every(
                    item => String(item.group?.value) !== String(option.value),
                  ),
                )
              }
            >
              + ADD
            </PrimaryButton>
          </Box>
        </HStack>
        <Stack spacing={4} px={6} py={5}>
          {visibleJitAccessItems.map(item => (
            <HStack key={item.id} align="center">
              <Box w={{ base: 'full', md: '38%' }}>
                <Select
                  options={getGroupOptions(item)}
                  value={getGroupValue(item)}
                  isLoading={isGroupsLoading}
                  onChange={value => handleGroupChange(item, value)}
                  showClearIndicator={false}
                  menuPortalTarget={document.body}
                  valueContainerIcon={<GroupIcon />}
                  onInputChange={value => {
                    setGroupSearch(value);
                    setVisibleGroupCount(15);
                  }}
                  onMenuScrollToBottom={() => {
                    if (
                      !groupSearch &&
                      visibleGroupCount < groupOptions.length
                    ) {
                      setVisibleGroupCount(count =>
                        Math.min(count + 15, groupOptions.length),
                      );
                    }
                  }}
                />
              </Box>
              <Box w={{ base: 'full', md: '48%' }}>
                <Select
                  options={cloudRoleOptions}
                  value={item.roles}
                  showTotalSelected
                  isMulti
                  placeholder="Select Snowflake Roles..."
                  selectedMessage={
                    'Snowflake role' +
                    (item.roles?.length === 1 ? '' : 's') +
                    ' selected'
                  }
                  onChange={value => onUpdateItem(item, 'roles', value)}
                  isLoading={isCloudRolesLoading}
                  menuPortalTarget={document.body}
                  valueContainerIcon={
                    <RoleIcon color={getIconColor(IconTypes.Snowflake)} />
                  }
                />
              </Box>
              <DeleteAttributeIconButton onClick={() => onRemoveItem(item)} />
            </HStack>
          ))}
        </Stack>
      </BorderedBox>
    </Stack>
  );
};
